// routes
import Router from './routes';
import { useEffect, useRef } from 'react';
// theme
import ThemeConfig from './theme';
import GlobalStyles from './theme/globalStyles';
// components
import ScrollToTop from './components/ScrollToTop';
import { BaseOptionChartStyle } from './components/charts/BaseOptionChart';

// PrimeReact Toast
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-indigo/theme.css'; // theme
import 'primereact/resources/primereact.min.css'; // core css
import 'primeicons/primeicons.css'; // icons

// Firebase imports
import { messaging } from './firebase';
import { onMessage } from 'firebase/messaging';
import { requestForToken } from './firebase';

// ----------------------------------------------------------------------

export default function App() {
  const toast = useRef(null);

  useEffect(() => {
    // Request permission and get token
    requestForToken();

    // Foreground listener using firebase onMessage
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('🎯 Foreground Notification:', payload);

      const title = payload?.notification?.title || 'Notification';
      const body = payload?.notification?.body || payload?.data?.message || '';
      const image = payload?.notification?.image || payload?.data?.image;

      // Determine notification type based on payload data
      const getNotificationType = () => {
        const type = payload?.data?.type?.toLowerCase();
        switch (type) {
          case 'success':
            return 'success';
          case 'warning':
            return 'warn';
          case 'error':
            return 'error';
          case 'info':
          default:
            return 'info';
        }
      };

      const severity = getNotificationType();

      // Show PrimeReact toast with custom styling
      if (toast.current) {
        toast.current.show({
          severity: severity,
          summary: title,
          detail: body,
          life: 6000,
          className: `custom-notification ${severity}-notification`,
          content: (props) => (
            <div className="custom-toast-content">
              {image && <img src={image} alt="Notification" className="notification-image" />}
              <div className="notification-text">
                <div className="notification-title">{props.message.summary}</div>
                <div className="notification-body">{props.message.detail}</div>
                {payload?.data?.timestamp && (
                  <div className="notification-time">
                    {new Date(payload.data.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
              {/* <button 
                className="notification-close p-link"
                onClick={() => toast.current.clear(props.message)}
              >
                <i className="pi pi-times"></i>
              </button> */}
            </div>
          )
        });
      }

      // Optional: Show browser notification
      if (Notification.permission === 'granted') {
        try {
          const options = {
            body: body,
            icon: image || '/logo192.png', // default app icon
            badge: '/logo192.png',
            tag: 'fcm-notification',
            requireInteraction: false,
            silent: false
          };
          new Notification(title, options);
        } catch (e) {
          console.warn('Browser Notification failed: ', e);
        }
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  return (
    <ThemeConfig>
      <Toast ref={toast} position="top-right" className="custom-toast-container" />
      <ScrollToTop />
      <GlobalStyles />
      <BaseOptionChartStyle />
      <Router />

      {/* Custom CSS for notifications */}
      <style jsx global>{`
        .custom-toast-container {
          .p-toast {
            min-width: 350px;
            max-width: 400px;
          }

          .p-toast-message {
            border-radius: 12px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
            border: none;
            margin-bottom: 1rem;
          }

          .p-toast-message-content {
            padding: 1rem;
            align-items: flex-start;
          }
        }

        .custom-toast-content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          width: 100%;
        }

        .notification-image {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          object-fit: fit;
          flex-shrink: 0;
        }

        .notification-text {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          font-weight: 600;
          font-size: 14px;
          line-height: 1.2;
          margin-bottom: 4px;
          color: #1f2937;
        }

        .notification-body {
          font-size: 13px;
          line-height: 1.4;
          color: #6b7280;
          margin-bottom: 4px;
          word-wrap: break-word;
        }

        .notification-time {
          font-size: 11px;
          color: #9ca3af;
          margin-top: 2px;
        }

        .notification-close {
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
          flex-shrink: 0;
          margin-left: auto;
        }

        .notification-close:hover {
          background: rgba(0, 0, 0, 0.1);
          color: #374151;
        }

        /* Custom severity styles */
        .info-notification {
          border-left: 4px solid #3b82f6;
        }

        .success-notification {
          border-left: 4px solid #10b981;
        }

        .warn-notification {
          border-left: 4px solid #f59e0b;
        }

        .error-notification {
          border-left: 4px solid #ef4444;
        }

        /* Animation improvements */
        .p-toast-message-enter {
          transform: translateX(100%);
          opacity: 0;
        }

        .p-toast-message-enter-active {
          transform: translateX(0);
          opacity: 1;
          transition: all 0.3s ease-out;
        }

        .p-toast-message-exit {
          transform: translateX(0);
          opacity: 1;
        }

        .p-toast-message-exit-active {
          transform: translateX(100%);
          opacity: 0;
          transition: all 0.3s ease-in;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .custom-toast-container .p-toast {
            min-width: 300px;
            max-width: 90vw;
            right: 1rem;
          }
        }

        @media (max-width: 480px) {
          .custom-toast-container .p-toast {
            min-width: 280px;
            max-width: 95vw;
          }

          .custom-toast-content {
            gap: 8px;
          }

          .notification-image {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>
    </ThemeConfig>
  );
}
